import { useState, useRef, useEffect, type ReactNode } from 'react';

export interface ActionItem {
  label: string;
  icon: string;
  iconColor?: string;
  danger?: boolean;
  onClick: () => void;
  disabled?: boolean;
}

interface ActionMenuProps {
  actions: ActionItem[];
  align?: 'left' | 'right';
}

export default function ActionMenu({ actions, align = 'right' }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
        title="Ações"
      >
        <i className="ri-more-2-fill text-lg" />
      </button>
      {open && (
        <div
          className={`absolute top-full mt-1 w-44 bg-white rounded-xl border border-background-200 shadow-lg z-50 py-1.5 overflow-hidden ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {actions.map((action, idx) => (
            <div key={action.label + idx}>
              {idx > 0 && action.danger && (
                <div className="mx-2 my-1 border-t border-background-100" />
              )}
              <button
                onClick={() => {
                  if (!action.disabled) {
                    setOpen(false);
                    action.onClick();
                  }
                }}
                disabled={action.disabled}
                className={`w-full px-3 py-2.5 text-left text-sm flex items-center gap-2.5 transition-colors cursor-pointer whitespace-nowrap ${
                  action.danger
                    ? 'text-red-600 hover:bg-red-50 disabled:opacity-40'
                    : 'text-foreground-700 hover:bg-background-50 disabled:opacity-40'
                }`}
              >
                <i
                  className={`${action.icon} text-base ${
                    action.danger
                      ? action.iconColor || 'text-red-500'
                      : action.iconColor || 'text-foreground-400'
                  }`}
                />
                <span className="font-medium">{action.label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}